import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BadgeCheck,
  Copy,
  MoreHorizontal,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  Trash2,
  X,
} from 'lucide-react';
import { modelRepository } from '../services/modelRepository';

const fmt = value => new Intl.NumberFormat().format(Number(value || 0));
const dt = value => {
  if(!value) return '—';
  const d=new Date(value);
  if(Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB',{
    day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit',hour12:false
  }).format(d).replace(',', ' ·');
};

function StatusPill({ value }) {
  const v=String(value||'draft').toLowerCase();
  const label={ready:'Ready',draft:'Draft',error:'Error',refreshing:'Refreshing'}[v]||value||'—';
  const cls=v==='ready'?'success':v==='error'?'danger':v==='refreshing'?'info':'neutral';
  return <span className={`status-pill compact ${cls}`}><i aria-hidden="true"/>{label}</span>;
}

function sourceMeta(model){
  const raw=String(model?.sourceType||model?.source?.type||'csv').toLowerCase();
  const system=raw.includes('jira')?'Jira':raw.includes('servicenow')?'ServiceNow':'CSV';
  const connection=model?.sourceLabel||model?.source?.label||model?.source?.fileName||'';
  return {system,connection};
}

function dataState(model){
  const configured=Number(model?.summary?.configuredCases||model?.summary?.requestedCases||model?.summary?.cases||0);
  const processed=Number(model?.summary?.processedCases||model?.summary?.cases||0);
  const raw=String(model?.status||'').toLowerCase();

  if(['processing','refreshing'].includes(raw)) return {key:'processing',label:'Processing',tone:'info',configured,processed};
  if(raw==='error') return {key:'failed',label:'Failed',tone:'danger',configured,processed};
  if(processed>0 && configured>processed) return {key:'partial',label:'Partial',tone:'warning',configured,processed};
  if(processed>0 && !(model?.persistence?.hasSavedData || model?.latestRuntime?.canonicalDataset)) return {key:'not-retained',label:'Processed · not saved',tone:'warning',configured,processed};
  if(processed>0) return {key:'processed',label:'Processed',tone:'success',configured,processed};
  return {key:'not-processed',label:'Not processed',tone:'neutral',configured,processed:0};
}

function hasSavedProcessedData(model){
  const processed=Number(model?.summary?.processedCases||model?.summary?.cases||0);
  return Boolean(processed>0 && (model?.persistence?.hasSavedData || model?.latestRuntime?.canonicalDataset));
}

function accessMeta(model){
  const current=String(model?.currentAccess?.source||'').toLowerCase();
  if(current==='subscription') return {label:'Subscription',tone:'success',title:'This model is covered by the active subscription for its source system'};
  if(current==='assessment'){
    const expires=model?.currentAccess?.expiresAt;
    const remaining=expires?Math.max(0,Math.ceil((new Date(expires).getTime()-Date.now())/86400000)):null;
    return {label:remaining==null?'Assessment':`Assessment · ${remaining}d`,tone:'success',title:expires?`Assessment active until ${new Date(expires).toLocaleDateString()}`:'Active 30-day assessment'};
  }
  const raw=String(
    model?.summary?.accessSource||
    model?.source?.accessSource||
    model?.accessSource||
    model?.entitlement?.source||
    ''
  ).toLowerCase();

  if(['subscription','instance_subscription','partner_client_slot'].includes(raw)) return {label:'Subscription',tone:'success',title:'Processing covered by subscription'};
  if(['assessment','assessment_credit','assessment-credits','credits'].includes(raw)) return {label:'Assessment',tone:'success',title:'Processed using an assessment'};
  if(['free_limited','free','free_assessment'].includes(raw)) return {label:'Free allowance',tone:'warning',title:'Processed using the free allowance'};
  if(['billing_parked','included','standard'].includes(raw)) return {label:'Free allowance',tone:'warning',title:'Processed using the free allowance'};

  // Older models may pre-date access-source persistence. Be explicit rather than
  // showing a blank value that looks like a loading/data error.
  if(hasSavedProcessedData(model) || Number(model?.summary?.processedCases||model?.summary?.cases||0)>0)
    return {label:'Not recorded',tone:'neutral',title:'This model was processed before processing-access tracking was recorded'};
  return {label:'Not processed',tone:'neutral',title:'Processing access is assigned when the model is processed'};
}

function DataStatusPill({ model }){
  const state=dataState(model);
  return <span className={`data-status-pill-v4 ${state.tone}`}><i aria-hidden="true"/>{state.label}</span>;
}

function AccessPill({ model }){
  const access=accessMeta(model);
  return <span className={`model-access-pill-v4 ${access.tone}`} title={access.title||''}>{access.label}</span>;
}

function ActionMenu({ model, anchorRef, onClose, onOpen, onEdit, onConfigure, onProcess, onActivateAssessment, canActivateAssessment, onDuplicate, onDelete }) {
  const [style,setStyle]=useState(null);

  useEffect(()=>{
    const position=()=>{
      const rect=anchorRef.current?.getBoundingClientRect();
      if(!rect)return;
      setStyle({top:rect.bottom+6,left:Math.max(8,rect.right-210)});
    };
    position();
    window.addEventListener('resize',position);
    window.addEventListener('scroll',position,true);
    return()=>{
      window.removeEventListener('resize',position);
      window.removeEventListener('scroll',position,true);
    };
  },[anchorRef]);

  useEffect(()=>{
    const close=e=>{
      if(anchorRef.current?.contains(e.target))return;
      onClose();
    };
    document.addEventListener('mousedown',close);
    return()=>document.removeEventListener('mousedown',close);
  },[anchorRef,onClose]);

  if(!style)return null;
  return createPortal(
    <div className="model-action-menu model-action-menu-v3" style={style} onMouseDown={e=>e.stopPropagation()}>
      {hasSavedProcessedData(model)&&<button onClick={()=>{onOpen(model);onClose();}}><Play size={15}/> Open in Advisor</button>}
      <button onClick={()=>{onEdit(model);onClose();}}><Pencil size={15}/> Edit details</button>
      <button onClick={()=>{onConfigure(model);onClose();}}><Settings2 size={15}/> Change data configuration</button>
      <button onClick={()=>{onProcess(model);onClose();}}><RefreshCw size={15}/> Process data</button>
      <button onClick={()=>{onDuplicate(model);onClose();}}><Copy size={15}/> Duplicate model</button>
      <div className="model-action-divider"/>
      <button className="danger" onClick={()=>{onDelete(model);onClose();}}><Trash2 size={15}/> Delete model</button>
    </div>,
    document.body
  );
}

function RowActions({ model, onOpen, onEdit, onConfigure, onProcess, onActivateAssessment, canActivateAssessment, onDuplicate, onDelete }) {
  const [open,setOpen]=useState(false);
  const ref=useRef(null);
  return <div className="model-row-actions-v3" onClick={e=>e.stopPropagation()}>
    <button ref={ref} title="Model actions" aria-label="Model actions" onClick={()=>setOpen(v=>!v)}><MoreHorizontal size={18}/></button>
    {open&&<ActionMenu model={model} anchorRef={ref} onClose={()=>setOpen(false)} onOpen={onOpen} onEdit={onEdit} onConfigure={onConfigure} onProcess={onProcess} onActivateAssessment={onActivateAssessment} canActivateAssessment={canActivateAssessment} onDuplicate={onDuplicate} onDelete={onDelete}/>}
  </div>;
}

function EditModelDialog({ model, onClose, onSave, onConfigure }) {
  const source=model?.source||{};
  const persistence=model?.persistence||{};
  const initialRetention=Boolean(persistence.retainProcessData ?? source.retainProcessData ?? source.storageConsent ?? false);

  const [name,setName]=useState(model.name||'');
  const [processType,setProcessType]=useState(model.processType||'');
  const [category,setCategory]=useState(model.category||'');
  const [entityLabel,setEntityLabel]=useState(source.entityLabel||'Case');
  const [description,setDescription]=useState(model.description||'');
  const [retainProcessData,setRetainProcessData]=useState(initialRetention);

  const save=()=>onSave(model,{
    name:name.trim(),
    processType:processType.trim(),
    category:category.trim(),
    description:description.trim(),
    source:{
      ...source,
      entityLabel:entityLabel.trim()||'Case',
      retainProcessData:Boolean(retainProcessData),
      storageConsent:Boolean(retainProcessData),
    },
    persistence:{
      ...persistence,
      modelDefinitionSaved:true,
      retainProcessData:Boolean(retainProcessData),
      storageConsent:Boolean(retainProcessData),
      retentionMode:retainProcessData?'workspace-data':'definition-only',
    },
  });

  return <div className="modal-backdrop model-edit-backdrop-v3" onMouseDown={onClose}>
    <div className="edit-model-modal edit-model-modal-v3" onMouseDown={e=>e.stopPropagation()}>
      <div className="edit-model-head v3">
        <div><span className="eyebrow">PROCESS MODEL</span><h2>Edit details</h2><p>Update the business and descriptive details for this model.</p></div>
        <button className="pa-icon-button" onClick={onClose}><X size={18}/></button>
      </div>

      <div className="edit-model-body v4">
        <div className="model-edit-grid-v4">
          <label>
            <span>Model name *</span>
            <input value={name} onChange={e=>setName(e.target.value)}/>
          </label>
          <label>
            <span>Process</span>
            <input value={processType} onChange={e=>setProcessType(e.target.value)} placeholder="e.g. Incident Management"/>
          </label>
          <label>
            <span>Category</span>
            <input value={category} onChange={e=>setCategory(e.target.value)} placeholder="e.g. ITSM"/>
          </label>
          <label>
            <span>Case / entity name</span>
            <input value={entityLabel} onChange={e=>setEntityLabel(e.target.value)} placeholder="Issue"/>
          </label>
          <label className="span-two">
            <span>Description</span>
            <textarea rows={2} value={description} onChange={e=>setDescription(e.target.value)} placeholder="Optional process context"/>
          </label>
        </div>

        <div className="model-edit-lower-v4">
          <label className={`model-retention-edit-v4 ${retainProcessData?'selected':''}`}>
            <input type="checkbox" checked={retainProcessData} onChange={e=>setRetainProcessData(e.target.checked)}/>
            <span>
              <b>Save processed data</b>
              <small>Keep processed cases, events and characteristics so the model can be reopened without extracting again.</small>
            </span>
          </label>

          <div className="model-config-shortcut-v4">
            <div>
              <b>Data configuration</b>
              <span>Projects, issue types, date/JQL scope, activity and characteristics.</span>
            </div>
            <button className="pa-secondary" onClick={()=>onConfigure(model)}><Settings2 size={15}/> Change</button>
          </div>
        </div>

        {initialRetention&&!retainProcessData&&
          <div className="model-retention-warning-v4">
            Saving this change removes retained process data. The model definition remains saved.
          </div>}
      </div>

      <div className="edit-model-foot v3">
        <button className="pa-secondary" onClick={onClose}>Cancel</button>
        <button className="pa-primary" disabled={!name.trim()} onClick={save}>Save changes</button>
      </div>
    </div>
  </div>;
}

export default function Models({ models, accessStatus, onOpen, onNew, onDelete, onDuplicate, onRefresh, onRefreshModel, onProcessModel, onEditModel, onActivateAssessment }) {
  const [query,setQuery]=useState('');
  const [dataStatus,setDataStatus]=useState('all');
  const [source,setSource]=useState('all');
  const [editing,setEditing]=useState(null);
  const availableAssessments=Number(accessStatus?.availableAssessments ?? accessStatus?.assessmentCredits?.balance ?? 0);
  // Do not hide the activation action based on a cached workspace balance. The
  // App refreshes billing status when activation is selected and the API is the
  // source of truth. This avoids a just-purchased assessment being invisible on
  // the Models screen until a full page reload.
  const canActivateAssessment=model=>!['subscription','assessment'].includes(String(model?.currentAccess?.source||'').toLowerCase());

  const rows=useMemo(()=>models.filter(m=>{
    const q=query.trim().toLowerCase();
    const meta=sourceMeta(m);
    const qok=!q||[m.name,m.category,m.processType,meta.system,meta.connection].some(v=>String(v||'').toLowerCase().includes(q));
    const sok=dataStatus==='all'||dataState(m).key===dataStatus;
    const raw=String(m.sourceType||m.source?.type||'csv').toLowerCase();
    const normalized=raw.includes('jira')?'jira':raw.includes('servicenow')?'servicenow':'csv';
    return qok&&sok&&(source==='all'||normalized===source);
  }),[models,query,dataStatus,source]);

  const edit=async model=>{
    try{setEditing(await modelRepository.getModel(model.id)||model);}
    catch(_){setEditing(model);}
  };

  const configure=model=>{
    setEditing(null);
    onRefreshModel(model);
  };

  const process=model=>{
    setEditing(null);
    onProcessModel(model);
  };

  return <>
    <div className="page-head models-page-head models-page-head-v3">
      <div>
        <h1>Models</h1>
        <p>Reusable process definitions and their latest processed data.</p>
      </div>
      <button className="pa-primary primary-large" onClick={onNew}><Plus size={17}/> New model</button>
    </div>

    <section className="models-card-v3">
      <div className="models-toolbar-v3">
        <div className="models-search-v3">
          <Search size={17}/>
          <input value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search models"/>
        </div>
        <select value={source} onChange={e=>setSource(e.target.value)} aria-label="Filter by source">
          <option value="all">All sources</option>
          <option value="csv">CSV</option>
          <option value="jira">Jira</option>
          <option value="servicenow">ServiceNow</option>
        </select>
        <select value={dataStatus} onChange={e=>setDataStatus(e.target.value)} aria-label="Filter by data status">
          <option value="all">All data statuses</option>
          <option value="processed">Processed</option>
          <option value="not-retained">Processed · not saved</option>
          <option value="partial">Partial</option>
          <option value="not-processed">Not processed</option>
          <option value="processing">Processing</option>
          <option value="failed">Failed</option>
        </select>
        <button className="pa-icon-button" title="Reload models" aria-label="Reload models" onClick={onRefresh}><RefreshCw size={17}/></button>
        <span>{rows.length} {rows.length===1?'model':'models'}</span>
      </div>

      {models.length?<>
        <div className="models-table-v3" role="table" aria-label="Process models">
          <div className="models-table-head-v3" role="row">
            <div role="columnheader">Model</div>
            <div role="columnheader">Source</div>
            <div role="columnheader" className="numeric">Cases</div>
            <div role="columnheader" className="numeric">Events</div>
            <div role="columnheader">Last processed</div>
            <div role="columnheader">Data status</div>
            <div role="columnheader" className="actions">Actions</div>
          </div>

          {rows.map(model=>{
            const meta=sourceMeta(model);
            const configuredCases=Number(model.summary?.configuredCases||model.summary?.requestedCases||model.summary?.cases||0);
            const processedCases=Number(model.summary?.processedCases||model.summary?.cases||0);
            const partial=Boolean(model.summary?.partialProcessing||configuredCases>processedCases);
            return <div
              key={model.id}
              className="models-table-row-v3"
              role="row"
              tabIndex={0}
              onClick={()=>onOpen(model)}
              onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();onOpen(model);}}}
            >
              <div className="models-name-v3" role="cell">
                <b>{model.name}</b>
                <span>{model.processType||'Process'}{model.category?` · ${model.category}`:''}</span>
              </div>
              <div className="models-source-v3" role="cell">
                <b>{meta.system}</b>
                {meta.connection&&<span>{meta.connection}</span>}
              </div>
              <div className="numeric models-count-v3" role="cell">
                <b>{fmt(processedCases)}</b>
                {partial&&<span>of {fmt(configuredCases)}</span>}
              </div>
              <div className="numeric models-count-v3" role="cell"><b>{fmt(model.summary?.events)}</b></div>
              <div className="models-date-v3" role="cell">{dt(model.lastRunAt || (processedCases>0 ? model.updatedAt : null))}</div>
              <div role="cell"><DataStatusPill model={model}/></div>
              <div role="cell" className="models-actions-cell-v4"><RowActions model={model} onOpen={onOpen} onEdit={edit} onConfigure={configure} onProcess={process} onActivateAssessment={onActivateAssessment} canActivateAssessment={canActivateAssessment(model)} onDuplicate={onDuplicate} onDelete={onDelete}/></div>
            </div>;
          })}
        </div>

        {!rows.length&&<div className="model-filter-empty"><Search size={20}/><b>No models match these filters</b><span>Clear the search or change the filters.</span></div>}
      </>:<div className="empty-state empty-state-feature ui1">
        <div className="empty-icon"><Plus size={20}/></div>
        <h3>Create your first process model</h3>
        <p>Connect a source or load process data to build a reusable model.</p>
        <button className="pa-primary" onClick={onNew}>New model</button>
      </div>}
    </section>

    {editing&&<EditModelDialog
      model={editing}
      onClose={()=>setEditing(null)}
      onConfigure={configure}
      onSave={async(m,patch)=>{await onEditModel(m,patch);setEditing(null);}}
    />}
  </>;
}
